// VER-218: Internal-admin account management (Step 11.8 + Step-11 review add-ons).
// - Table of client tenants with an active/inactive toggle (deactivate = the clean
//   way to offboard a whole client; step-up-gated).
// - "New client account" opens a create -> invite-first-admin flow (cross-tenant
//   invite from Step 8.4, so the first admin lands in the NEW account).
// Scoped to internal_admin (nav-hidden + route-guarded).
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  App,
  Card,
  Table,
  Tag,
  Switch,
  Space,
  Button,
  Modal,
  Steps,
  Form,
  Input,
  Select,
  Result,
  Typography,
} from "antd";
import { ReloadOutlined, PlusOutlined } from "@ant-design/icons";
import { accountApi, invitesAdminApi, brandsApi } from "../../console/consoleApi";
import { useStepUp } from "../../console/useStepUp";
import { errorMessage } from "../../console/consoleUtils";

const { Title, Text } = Typography;

const isCancelled = (err) => err?.message === "STEP_UP_CANCELLED";

const normalizeDomains = (vals) => [
  ...new Set((vals ?? []).map((v) => v.trim().toLowerCase().replace(/^@/, "")).filter(Boolean)),
];

export default function ConsoleAccounts() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { withStepUp, modal } = useStepUp();

  const [accounts, setAccounts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const brandName = (id) => brands.find((b) => b.id === id)?.displayName;

  // Create + invite-first-admin flow (inside the modal).
  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [created, setCreated] = useState(null);
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [accForm] = Form.useForm();
  const [invForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [accRes, brandRes] = await Promise.all([accountApi.listAll(), brandsApi.list()]);
      setAccounts(accRes.data.data.accounts);
      setBrands(brandRes.data.data.brands);
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleActive = async (row, value) => {
    setTogglingId(row.id);
    try {
      const res = await withStepUp((token) => accountApi.setActive(row.id, value, token));
      const updated = res.data.data.account;
      setAccounts((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      message.success(value ? "Account reactivated." : "Account deactivated.");
    } catch (err) {
      if (!isCancelled(err)) message.error(errorMessage(err));
    } finally {
      setTogglingId(null);
    }
  };

  const openCreate = () => {
    setCreated(null);
    setStep(0);
    accForm.resetFields();
    invForm.resetFields();
    setModalOpen(true);
  };

  const createAccount = async (values) => {
    setCreating(true);
    try {
      const res = await accountApi.create({
        name: values.name.trim(),
        type: values.type ?? "external",
        allowedDomains: normalizeDomains(values.allowedDomains),
        brandId: values.brandId ?? null,
      });
      setCreated(res.data.data.account);
      message.success(`Account “${res.data.data.account.name}” created.`);
      setStep(1);
      load(); // refresh the table behind the modal
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const inviteAdmin = async (values) => {
    setInviting(true);
    try {
      await invitesAdminApi.create({
        email: values.email.trim(),
        name: values.name?.trim() || undefined,
        // First admin matches the account type: external -> account_admin, internal -> internal_admin.
        role: created?.type === "internal" ? "internal_admin" : "account_admin",
        accountId: created.id, // cross-tenant: lands in the NEW account
      });
      message.success(`Invite sent to ${values.email.trim()}.`);
      setStep(2);
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setInviting(false);
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      // Click the name to drill into the company and manage its people.
      render: (name, row) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/console/accounts/${row.id}`)}>
          {name}
        </Button>
      ),
    },
    { title: "ID", dataIndex: "id", key: "id", width: 70 },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (t) => (
        <Tag color={t === "internal" ? "geekblue" : "default"}>{t || "external"}</Tag>
      ),
    },
    {
      title: "Allowed domains",
      dataIndex: "allowedDomains",
      key: "allowedDomains",
      render: (d) =>
        d?.length ? d.map((x) => <Tag key={x}>{x}</Tag>) : <Text type="secondary">any</Text>,
    },
    {
      title: "Brand",
      dataIndex: "brandId",
      key: "brandId",
      // Filter the list to a single brand (or "Default" = no brand assigned).
      filters: [
        { text: "Default (VerifyU)", value: "__default__" },
        ...brands.map((b) => ({ text: b.displayName, value: b.id })),
      ],
      onFilter: (value, record) =>
        value === "__default__" ? record.brandId == null : record.brandId === value,
      render: (id) =>
        id && brandName(id) ? brandName(id) : <Text type="secondary">default</Text>,
    },
    {
      title: "Active",
      key: "active",
      align: "center",
      render: (_, row) => (
        <Switch
          checked={row.isActive}
          loading={togglingId === row.id}
          onChange={(v) => toggleActive(row, v)}
        />
      ),
    },
  ];

  return (
    <Card
      title={
        <Title level={4} style={{ margin: 0 }}>
          Accounts
        </Title>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            New account
          </Button>
        </Space>
      }
    >
      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={accounts}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
      />

      <Modal
        title="New account"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        destroyOnHidden
        width={560}
      >
        <Steps
          current={step}
          size="small"
          style={{ margin: "12px 0 24px" }}
          items={[{ title: "Create account" }, { title: "Invite first admin" }, { title: "Done" }]}
        />

        {step === 0 && (
          <Form
            form={accForm}
            layout="vertical"
            onFinish={createAccount}
            requiredMark={false}
            initialValues={{ type: "external" }}
          >
            <Form.Item
              name="name"
              label="Account name"
              rules={[{ required: true, message: "Enter an account name" }]}
            >
              <Input placeholder="Acme Corp" />
            </Form.Item>
            <Form.Item
              name="type"
              label="Account type"
              help="External = a client organisation. Internal = a VerifyU tenant (Ops, etc.). This can't be changed later."
            >
              <Select
                options={[
                  { value: "external", label: "External (client)" },
                  { value: "internal", label: "Internal (VerifyU)" },
                ]}
              />
            </Form.Item>
            <Form.Item
              name="allowedDomains"
              label="Allowed email domains"
              help="Invites for this account are restricted to these domains. Leave empty to allow any."
            >
              <Select mode="tags" placeholder="e.g. acme.com" tokenSeparators={[",", " "]} open={false} />
            </Form.Item>
            <Form.Item
              name="brandId"
              label="Brand"
              help="Which brand's email identity this account uses. Leave empty for Default (VerifyU) — you can change it later on the account page."
            >
              <Select
                allowClear
                placeholder="Default (VerifyU)"
                options={brands.map((b) => ({ value: b.id, label: b.displayName }))}
              />
            </Form.Item>
            <Button type="primary" htmlType="submit" loading={creating}>
              Create account
            </Button>
          </Form>
        )}

        {step === 1 && created && (
          <Form form={invForm} layout="vertical" onFinish={inviteAdmin} requiredMark={false}>
            <Text type="secondary">
              Invite the first <b>Account Admin</b> for <b>{created.name}</b>. As Internal Admin
              you can invite any domain — the account’s allowlist doesn’t block you.
            </Text>
            <Form.Item name="name" label="Name (optional)" style={{ marginTop: 16 }}>
              <Input placeholder="Jane Doe" autoComplete="off" />
            </Form.Item>
            <Form.Item
              name="email"
              label="Admin email"
              rules={[{ required: true, type: "email", message: "Enter a valid email" }]}
            >
              <Input placeholder="admin@acme.com" autoComplete="off" />
            </Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={inviting}>
                Send invite
              </Button>
              <Button onClick={() => setStep(2)}>Skip for now</Button>
            </Space>
          </Form>
        )}

        {step === 2 && (
          <Result
            status="success"
            title="Client onboarded"
            subTitle={created ? `${created.name} is set up.` : undefined}
            extra={
              <Button type="primary" onClick={() => setModalOpen(false)}>
                Done
              </Button>
            }
          />
        )}
      </Modal>

      {/* Step-up (re-OTP) modal for activate/deactivate */}
      {modal}
    </Card>
  );
}
